// Design placement in Smart Object content pixels ("design space").
// A placement is { cx, cy, width, height, rotation } — centre, size and
// rotation in degrees about the centre.

export const MIN_SCALE = 0.02;
export const MAX_SCALE = 10;

const rad = (deg) => (deg * Math.PI) / 180;

/** Size of an image scaled to fit (or fill) the design area, keeping aspect. */
export function scaledSize(designW, designH, imgW, imgH, mode = "fit") {
  const s = mode === "fill" ? Math.max(designW / imgW, designH / imgH) : Math.min(designW / imgW, designH / imgH);
  return { width: imgW * s, height: imgH * s };
}

export function centeredPlacement(designW, designH, imgW, imgH, mode = "fit") {
  const { width, height } = scaledSize(designW, designH, imgW, imgH, mode);
  return { cx: designW / 2, cy: designH / 2, width, height, rotation: 0 };
}

/** Half extents of the rotated rectangle's axis-aligned bounding box. */
export function halfExtents(p) {
  const c = Math.abs(Math.cos(rad(p.rotation))), s = Math.abs(Math.sin(rad(p.rotation)));
  return { hx: (p.width * c + p.height * s) / 2, hy: (p.width * s + p.height * c) / 2 };
}

/**
 * Keeps the design on the print area so it can't be dragged out of sight:
 * along each axis it must overlap the area by half of the smaller of the
 * two (a small design keeps its centre inside; a large one can be panned
 * until its edge reaches the area's middle).
 */
export function keepInArea(p, designW, designH) {
  const { hx, hy } = halfExtents(p);
  const limit = (c, h, size) => {
    const keep = Math.min(2 * h, size) / 2;
    return Math.max(keep - h, Math.min(size + h - keep, c));
  };
  const cx = limit(p.cx, hx, designW), cy = limit(p.cy, hy, designH);
  return cx === p.cx && cy === p.cy ? p : { ...p, cx, cy };
}

export function align(p, designW, designH, edge) {
  const { hx, hy } = halfExtents(p);
  switch (edge) {
    case "left": return { ...p, cx: hx };
    case "hcenter": return { ...p, cx: designW / 2 };
    case "right": return { ...p, cx: designW - hx };
    case "top": return { ...p, cy: hy };
    case "vcenter": return { ...p, cy: designH / 2 };
    case "bottom": return { ...p, cy: designH - hy };
    default: return p;
  }
}

/** Corners in design space: top-left, top-right, bottom-right, bottom-left. */
export function corners(p) {
  const c = Math.cos(rad(p.rotation)), s = Math.sin(rad(p.rotation));
  return [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([sx, sy]) => {
    const x = (sx * p.width) / 2, y = (sy * p.height) / 2;
    return { x: p.cx + x * c - y * s, y: p.cy + x * s + y * c };
  });
}

/** Point at local fraction (fx, fy) ∈ [0, 1]² of the placed rectangle. */
export function pointAt(p, fx, fy) {
  const c = Math.cos(rad(p.rotation)), s = Math.sin(rad(p.rotation));
  const x = (fx - 0.5) * p.width, y = (fy - 0.5) * p.height;
  return { x: p.cx + x * c - y * s, y: p.cy + x * s + y * c };
}

export function scaleAboutCenter(p, factor, natural) {
  // Clamp relative to the image's natural "fit" size.
  const ratio = p.width / natural.width;
  const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, ratio * factor));
  const f = next / ratio;
  return { ...p, width: p.width * f, height: p.height * f };
}

export const normalizeAngle = (deg) => {
  let a = deg % 360;
  if (a > 180) a -= 360;
  if (a <= -180) a += 360;
  return a;
};

/**
 * Applies the reference design's sizing and relative position to another
 * design (the old editor's "Apply to all").
 */
export function matchPlacement(reference, refMode, target, designW, designH) {
  let size;
  if (refMode === "fill" || refMode === "fit") size = scaledSize(designW, designH, target.naturalWidth, target.naturalHeight, refMode);
  else {
    // Custom: match the reference area while keeping the target aspect ratio.
    const s = Math.sqrt((reference.width * reference.height) / (target.naturalWidth * target.naturalHeight));
    size = { width: target.naturalWidth * s, height: target.naturalHeight * s };
  }
  return { cx: reference.cx, cy: reference.cy, width: size.width, height: size.height, rotation: reference.rotation };
}

/**
 * Converts a placement from the previous editor's top-left Konva model
 * ({ x, y, width, height, rotation } rotating about the top-left corner).
 */
export function fromTopLeft({ x, y, width, height, rotation = 0 }) {
  const c = Math.cos(rad(rotation)), s = Math.sin(rad(rotation));
  return { cx: x + (width / 2) * c - (height / 2) * s, cy: y + (width / 2) * s + (height / 2) * c, width, height, rotation };
}

/** Draws the placed image into a design-space canvas context. */
export function drawPlaced(ctx, image, p, scale = 1) {
  ctx.save();
  ctx.scale(scale, scale);
  ctx.translate(p.cx, p.cy);
  ctx.rotate(rad(p.rotation));
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, -p.width / 2, -p.height / 2, p.width, p.height);
  ctx.restore();
}

/**
 * Placement independent of the design-area size, for reusing one placement
 * across different mockups: centre as a fraction of the area, size relative
 * to the image's "fit" size, and the sizing mode.
 */
export function normalizePlacement(p, mode, designW, designH, imgW, imgH) {
  const fit = scaledSize(designW, designH, imgW, imgH, "fit");
  return { mode, fx: p.cx / designW, fy: p.cy / designH, scale: p.width / fit.width, rotation: p.rotation };
}

export function denormalizePlacement(n, designW, designH, imgW, imgH) {
  if (!n || n.mode === "fit" || n.mode === "fill") return centeredPlacement(designW, designH, imgW, imgH, n?.mode || "fit");
  const fit = scaledSize(designW, designH, imgW, imgH, "fit");
  return { cx: n.fx * designW, cy: n.fy * designH, width: fit.width * n.scale, height: fit.height * n.scale, rotation: n.rotation };
}
